<template>
  <div class="page">

    <!-- 顶部栏 -->
    <header class="topbar">
      <div class="tb-back" @click="$router.back()">
        <i class="fa-solid fa-chevron-left"></i>
      </div>
      <h1 class="tb-title">商品详情</h1>
      <FavGuide v-model:visible="showFavGuide" theme="light" />
    </header>

    <!-- 加载中 -->
    <div v-if="loading" class="loading">
      <i class="fa-solid fa-spinner fa-spin"></i>
      <span>加载中...</span>
    </div>

    <template v-else-if="product">

      <!-- 商品图片 -->
      <div class="prod-banner">
        <img
          :src="product.image ? imgUrl(product.image) : '/no_pic.png'"
          :alt="product.name"
        />
        <span v-if="product.tag" class="prod-tag">{{ product.tag }}</span>
      </div>

      <!-- 价格区域 -->
      <div class="price-card">
        <div class="pc-left">
          <p class="pc-label">补贴价</p>
          <p class="pc-price">¥{{ product.subsidy_price || product.price }}</p>
          <p v-if="product.original_price" class="pc-orig">
            <s>¥{{ product.original_price }}</s>
            <span class="pc-discount" v-if="discount">{{ discount }}折</span>
          </p>
        </div>
      </div>

      <!-- 基本信息 -->
      <div class="info-card">
        <h2 class="ic-name">{{ product.name }}</h2>
        <div v-if="product.description" class="ic-desc markdown-body" v-html="descriptionHtml"></div>
        <p v-else class="ic-desc">优质商品，值得信赖。政府补贴，优惠到家。</p>
        <div class="ic-tags">
          <span v-if="product.tag" class="ic-tag blue">{{ product.tag }}</span>
          <span v-if="product.merchant_name" class="ic-tag gray">{{ product.merchant_name }}</span>
          <span v-if="product.category" class="ic-tag gray">{{ product.category }}</span>
        </div>
      </div>

      <!-- 补贴政策 -->
      <div class="section-card subsidy-card">
        <h3 class="sc-title"><i class="fa-solid fa-award"></i> 补贴政策</h3>
        <div class="subsidy-info">
          <div class="si-row">
            <i class="fa-solid fa-circle-check"></i>
            <span>符合以旧换新条件可享受政府补贴</span>
          </div>
          <div class="si-row">
            <i class="fa-solid fa-circle-check"></i>
            <span>补贴金额直接抵扣商品价格</span>
          </div>
          <div class="si-row">
            <i class="fa-solid fa-circle-check"></i>
            <span>线上申请，线下核销，方便快捷</span>
          </div>
        </div>
      </div>

      <!-- 所属商户 -->
      <div v-if="product.merchant" class="section-card shop-card" @click="goShop">
        <h3 class="sc-title"><i class="fa-solid fa-store"></i> 所属商户</h3>
        <div class="shop-row">
          <div class="shop-avatar">
            <i class="fa-solid fa-store"></i>
          </div>
          <div class="shop-info">
            <h4>{{ product.merchant.name || product.merchant_name }}</h4>
            <p>{{ product.merchant.town?.name || '' }} · {{ product.merchant.address || '' }}</p>
          </div>
          <i class="fa-solid fa-chevron-right shop-arrow"></i>
        </div>
      </div>

      <!-- 底部操作栏 -->
      <div class="action-bar">
        <button class="ab-btn ab-shop" @click="goShop">
          <i class="fa-solid fa-store"></i>
          <span>查看商户</span>
        </button>
        <button class="ab-btn ab-share" @click="showShareTip = true">
          <i class="fa-solid fa-share-nodes"></i>
          <span>分享</span>
        </button>
      </div>

    </template>

    <!-- 空状态 -->
    <div v-else class="empty">
      <i class="fa-solid fa-box-open"></i>
      <p>商品信息不存在</p>
    </div>

    <!-- 分享提示 -->
    <ShareTip v-model:visible="showShareTip" />

  </div>
</template>

<script>
import { marked } from 'marked'
import { initWechat, setShareData } from '../utils/wechat'
import ShareTip from '@/components/ShareTip.vue'
import FavGuide from '@/components/FavGuide.vue'

export default {
  name: 'ProductDetail',
  components: { ShareTip, FavGuide },

  data() {
    return {
      product: null,
      loading: true,
      showShareTip: false,
      showFavGuide: false,
    }
  },

  computed: {
    discount() {
      if (this.product?.original_price && this.product?.subsidy_price) {
        return ((this.product.subsidy_price / this.product.original_price) * 10).toFixed(1)
      }
      return null
    },
    descriptionHtml() {
      if (!this.product?.description) return ''
      return marked(this.product.description)
    }
  },

  mounted() {
    this.fetchProduct()
  },

  methods: {
    async fetchProduct() {
      this.loading = true
      const id = this.$route.params.id
      try {
        const r = await this.$api.get(`/api/v1/products/${id}`)
        if (r.data.code === 0 && r.data.data) {
          const p = r.data.data
          this.product = {
            ...p,
            merchant_name: p.merchant?.name || ''
          }
          this.initShare()
        }
      } catch (e) {
        console.error(e)
      } finally {
        this.loading = false
      }
    },

    async initShare() {
      const wx = await initWechat()
      if (wx && this.product) {
        setShareData(wx, {
          title: this.product.name || '商品详情',
          desc: this.product.description || `${this.product.name} - 数聚吴川`,
          link: window.location.href,
          imgUrl: this.product.image || ''
        })
      }
    },

    imgUrl(path) {
      if (!path) return ''
      if (path.startsWith('http')) return path
      const base = process.env.VUE_APP_API_BASE_URL || 'http://localhost:3000'
      return base + (path.startsWith('/') ? path : '/' + path)
    },

    goodsIcon(name) {
      if (/电扇|风扇/.test(name)) return 'fa-solid fa-fan'
      if (/月饼/.test(name)) return 'fa-solid fa-gift'
      if (/饭煲|电饭/.test(name)) return 'fa-solid fa-kitchen-set'
      if (/炸锅/.test(name)) return 'fa-solid fa-bowl-rice'
      if (/手机/.test(name)) return 'fa-solid fa-mobile-screen'
      if (/电视/.test(name)) return 'fa-solid fa-tv'
      return 'fa-solid fa-box-open'
    },

    goShop() {
      if (this.product?.merchant?.id) {
        this.$router.push(`/shop/${this.product.merchant.id}`)
      }
    }
  }
}
</script>

<style scoped>
@import url('https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css');

*, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }

.page {
  width: 100%;
  min-height: 100vh;
  background: #f4f8fb;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'PingFang SC', 'Microsoft YaHei', sans-serif;
  color: #1f2937;
  padding-bottom: calc(64px + env(safe-area-inset-bottom, 0px));
}

/* ================================================================
   顶部栏
   ================================================================ */
.topbar {
  position: sticky;
  top: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  height: 48px;
  padding: 0 12px;
  background: #fff;
  border-bottom: 1px solid #f3f4f6;
}

.tb-back {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  cursor: pointer;
  color: #1f2937;
}

.tb-back:active { background: #f3f4f6; }
.tb-back i { font-size: 14px; }

.tb-title {
  flex: 1;
  text-align: center;
  font-size: 16px;
  font-weight: 700;
}

.tb-spacer { width: 32px; }

/* ================================================================
   商品图片
   ================================================================ */
.prod-banner {
  width: 100%;
  height: 280px;
  background: linear-gradient(135deg, #dbeafe, #bfdbfe);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
}

.prod-banner img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.prod-banner > i {
  font-size: 72px;
  color: #93c5fd;
}

.prod-tag {
  position: absolute;
  top: 12px;
  left: 0;
  background: linear-gradient(135deg, #ef4444, #dc2626);
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  padding: 5px 14px 5px 10px;
  border-radius: 0 12px 12px 0;
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.4);
  animation: tagPulse 2s ease-in-out infinite;
}

@keyframes tagPulse {
  0%, 100% {
    box-shadow: 0 2px 8px rgba(239, 68, 68, 0.4);
  }
  50% {
    box-shadow: 0 2px 12px rgba(239, 68, 68, 0.6), 0 0 16px rgba(239, 68, 68, 0.2);
  }
}

/* ================================================================
   价格区域
   ================================================================ */
.price-card {
  background: linear-gradient(135deg, #dc2626, #ef4444);
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  color: #fff;
}

.pc-label {
  font-size: 12px;
  opacity: .8;
}

.pc-price {
  font-size: 36px;
  font-weight: 900;
  line-height: 1.1;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  animation: priceBreathe 3s ease-in-out infinite;
}

@keyframes priceBreathe {
  0%, 100% {
    transform: scale(1);
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }
  50% {
    transform: scale(1.02);
    text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  }
}

.pc-orig {
  font-size: 12px;
  opacity: .7;
  margin-top: 2px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.pc-discount {
  background: linear-gradient(135deg, #fbbf24, #f59e0b);
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
  color: #92400e;
  box-shadow: 0 1px 4px rgba(245, 158, 11, 0.3);
}

/* ================================================================
   基本信息
   ================================================================ */
.info-card {
  background: #fff;
  margin: 0;
  padding: 16px;
}

.ic-name {
  font-size: 18px;
  font-weight: 800;
  line-height: 1.3;
}

.ic-desc {
  font-size: 13px;
  color: #6b7280;
  margin-top: 6px;
  line-height: 1.6;
}

.markdown-body {
  font-size: 13px;
  color: #6b7280;
  line-height: 1.7;

  h1, h2, h3, h4 {
    margin: 12px 0 8px;
    font-weight: 600;
    color: #374151;
  }

  h1 { font-size: 20px; }
  h2 { font-size: 18px; }
  h3 { font-size: 16px; }
  h4 { font-size: 14px; }

  p {
    margin: 8px 0;
  }

  ul, ol {
    padding-left: 20px;
    margin: 8px 0;
  }

  li {
    margin: 4px 0;
  }

  code {
    background: #f3f4f6;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 12px;
    color: #e11d48;
  }

  pre {
    background: #1f2937;
    color: #e5e7eb;
    padding: 12px;
    border-radius: 8px;
    overflow-x: auto;
    margin: 8px 0;

    code {
      background: transparent;
      color: inherit;
      padding: 0;
    }
  }

  blockquote {
    border-left: 3px solid #2563eb;
    padding-left: 12px;
    margin: 8px 0;
    color: #6b7280;
  }

  a {
    color: #2563eb;
    text-decoration: none;
  }

  img {
    max-width: 100%;
    height: auto;
    border-radius: 8px;
    margin: 8px 0;
    display: block;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    margin: 8px 0;

    th, td {
      border: 1px solid #e5e7eb;
      padding: 8px;
      text-align: left;
    }

    th {
      background: #f9fafb;
      font-weight: 600;
    }
  }
}

.ic-tags {
  display: flex;
  gap: 6px;
  margin-top: 10px;
  flex-wrap: wrap;
}

.ic-tag {
  font-size: 10px;
  padding: 3px 8px;
  border-radius: 6px;
  font-weight: 500;
}

.ic-tag.blue { background: #eff6ff; color: #2563eb; }
.ic-tag.gray { background: #f3f4f6; color: #6b7280; }

/* ================================================================
   内容卡片
   ================================================================ */
.section-card {
  background: #fff;
  margin-top: 10px;
  padding: 14px 16px;
}

.sc-title {
  font-size: 15px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 12px;
}

.sc-title i { color: #2563eb; font-size: 14px; }

/* ================================================================
   补贴政策
   ================================================================ */
.subsidy-card {
  background: #fffbeb;
  border: 1px solid #fde68a;
}

.subsidy-info {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.si-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #92400e;
}

.si-row i { color: #16a34a; font-size: 14px; }

/* ================================================================
   所属商户
   ================================================================ */
.shop-card { cursor: pointer; }
.shop-card:active { background: #f9fafb; }

.shop-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.shop-avatar {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: #eff6ff;
  display: flex;
  align-items: center;
  justify-content: center;
}

.shop-avatar i { font-size: 18px; color: #3b82f6; }

.shop-info {
  flex: 1;
  min-width: 0;
}

.shop-info h4 {
  font-size: 14px;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.shop-info p {
  font-size: 11px;
  color: #9ca3af;
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.shop-arrow { font-size: 12px; color: #d1d5db; }

/* ================================================================
   底部操作栏
   ================================================================ */
.action-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  gap: 10px;
  padding: 12px 16px calc(12px + env(safe-area-inset-bottom, 0px));
  background: #fff;
  border-top: 1px solid #f3f4f6;
  z-index: 50;
}

.ab-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 12px 20px;
  border: none;
  border-radius: 24px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  flex: 1;
}

.ab-shop {
  background: #f3f4f6;
  color: #374151;
}

.ab-shop:active { background: #e5e7eb; }

.ab-share {
  background: #07c160;
  color: #fff;
}

.ab-share:active { background: #06ad56; }

/* ================================================================
   空状态 / 加载
   ================================================================ */
.empty, .loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 0;
  color: #9ca3af;
}

.empty i, .loading i {
  font-size: 40px;
  margin-bottom: 12px;
  opacity: .5;
}

/* ================================================================
   响应式
   ================================================================ */
@media (max-width: 360px) {
  .prod-banner { height: 220px; }
  .pc-price { font-size: 28px; }
  .ic-name { font-size: 16px; }
}

@media (min-width: 481px) {
  .prod-banner { height: 340px; }
  .price-card { padding: 20px; }
  .pc-price { font-size: 36px; }
  .info-card, .section-card { padding: 18px 20px; }
  .ic-name { font-size: 20px; }
}
</style>
